import { AuthBrandPanel } from '@/components/AuthBrandPanel';
import { LangSwitch } from '@/components/LangSwitch';
import { ThemeMenu } from '@/components/ThemeMenu';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

/**
 * Pre-auth shell: two columns, split by a hairline — the form on one side and
 * the product on the other (`AuthBrandPanel`). Same shape as the sign-in
 * reference the console is built to match, minus its dashboard screenshot.
 *
 * The panel is desktop-only: below 1024px the shell is the form column alone,
 * with the page's own lockup above the form carrying the branding. That is also
 * why the language and theme controls float at the page's top-right instead of
 * sitting in a bar — there is no bar to sit in, and on a phone they must not
 * push the form down the screen.
 *
 * `panelSide` is which column the panel takes. `/login` reads form-first;
 * `/pending` is the end of the same flow and mirrors it, so the reader can tell
 * the two states apart before reading a word.
 */
export function AuthLayout({ panelSide = 'right' }: { panelSide?: 'left' | 'right' }) {
  const { t } = useTranslation();

  return (
    <div className="auth-shell">
      <div className="auth-controls">
        <LangSwitch />
        <ThemeMenu />
      </div>

      <div className={panelSide === 'left' ? 'auth-grid auth-grid-flip' : 'auth-grid'}>
        <div className="auth-form-col">
          {/* `Outlet` is the form column's flexible middle: the copyright below
              it is the column's own last line, so the two-column height comes
              from the page rather than from a footer band across both. */}
          <div className="auth-form-slot">
            <Outlet />
          </div>

          <p className="auth-copyright">
            {t('common.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>

        <AuthBrandPanel />
      </div>
    </div>
  );
}
